import type { DynamicFlatNode } from 'commons';
import type { Tree } from './tree.model';

export namespace TreeStateActions {
  export class AddNodes {
    static readonly type = '[TREE] add node';
    /**
     * @param nodes The nodes to be added.
     */
    constructor(
      public readonly nodes: DynamicFlatNode[],
      public readonly index: number
    ) {}
  }
  /**
   * Action to refresh a specific node in the tree.
   */
  export class RefreshNode {
    static readonly type = '[TREE] refreshNode';
    /**
     * @param node The node to be refreshed.
     */
    constructor(public readonly node: DynamicFlatNode) {}
  }

  /**
   * Action to end multi-selection mode in the tree.
   */
  export class EndMultiSelection {
    static readonly type = '[TREE] endMultiSelection]';
    /**
     * @param index The index at which multi-selection ends.
     */
    constructor(public readonly index: number) {}
  }

  /**
   * Action to start multi-selection mode in the tree.
   */
  export class StartMultiSelection {
    static readonly type = '[TREE] startMultiSelection]';
    /**
     * @param index The index at which multi-selection starts.
     */
    constructor(public readonly index: number) {}
  }

  /**
   * Action to stop multi-selection mode in the tree.
   */
  export class StopMultiSelection {
    static readonly type = '[TREE] stopMultiSelection]';
  }

  /**
   * Action to save nodes by type in the tree.
   */
  export class SaveNodesByType {
    static readonly type = '[TREE] save tree by type';
    /**
     * @param tree The tree structure to be saved.
     */
    constructor(public readonly tree: Tree) {}
  }

  /**
   * Action to save the entire flat tree.
   */
  export class SaveFlatTree {
    static readonly type = '[TREE] save entire flat tree';
    /**
     * @param flatTree The flat tree structure to be saved.
     */
    constructor(public readonly flatTree: DynamicFlatNode[]) {}
  }

  /**
   * Action to hide all nodes in the tree.
   */
  export class HideAllNodes {
    static readonly type = '[TREE] hide All Nodes';
  }

  /**
   * Action to clear all nodes in the tree.
   */
  export class ClearNodes {
    static readonly type = '[TREE] clear all Nodes';
  }

  /**
   * Action to toggle the visibility of specific nodes in the tree.
   */
  export class ToggleNodeVisibility {
    static readonly type = '[TREE] toggle Node Visibility';
    /**
     * @param opt Options containing nodes to be toggled and visibility state.
     */
    constructor(public readonly opt: { nodes: DynamicFlatNode[]; hide: boolean }) {}
  }

  export class DeleteNodes {
    static readonly type = '[TREE] delete node';
    /**
     * @param nodes The nodes to be deleted.
     */
    constructor(public readonly nodes: DynamicFlatNode[]) {}
  }

  /**
   * Action to toggle the loading state of the tree.
   */
  export class ToggleTreeLoading {
    static readonly type = '[TREE] toggle tree loading';
  }

  /**
   * Action to set the loading state of the tree.
   */
  export class SetTreeLoading {
    static readonly type = '[TREE] set tree loading';
  }

  /**
   * Action to unset the loading state of the tree.
   */
  export class UnsetTreeLoading {
    static readonly type = '[TREE] unset tree loading';
  }

  /**
   * Action to set the loading state on a specific node.
   */
  export class SetNodeLoading {
    static readonly type = '[TREE] set node loading';
    /**
     * @param node The node on which to set the loading state.
     * @param isLoading The loading state to be set.
     */
    constructor(
      public readonly node: DynamicFlatNode,
      public readonly isLoading: boolean
    ) {}
  }

  /**
   * Action to toggle the error state on a specific node.
   */
  export class ToggleErrorOnNode {
    static readonly type = '[TREE] toggle error on node';
    /**
     * @param node The node on which to toggle the error state.
     * @param isError The error state to be set.
     */
    constructor(
      public readonly node: DynamicFlatNode,
      public readonly isError = true
    ) {}
  }

  /**
   * Action for the initial setup of the tree.
   */
  export class InitalSetup {
    static readonly type = '[TREE] inital setup';
  }
}
